import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import panelApi from "@/lib/panelApi";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, ArrowLeft, Download, CheckCircle2, AlertTriangle, ClipboardPaste } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

/**
 * BulkImport — lets the user paste a spreadsheet range or drop a CSV file
 * and create many reservations in one click.
 *
 * Turkish column headers (case-insensitive):
 *   Grup Adı | Giriş | Çıkış | Yetişkin | Çocuk | Görevli
 *   | 1K | 2K | 3K | 4K | 5K | Başlangıç | Bitiş | Not
 *
 * Excel/Google Sheets copy is tab-separated by default; we also accept
 * semicolons and commas.
 */
const MEAL_MAP = {
  kahvalti: "kahvalti", kahvaltı: "kahvalti",
  ogle: "ogle", öğle: "ogle", "ögle": "ogle",
  aksam: "aksam", akşam: "aksam",
};

const HEADER_ALIASES = {
  "grup adı": "group_name", "grup adi": "group_name", "grup": "group_name", "musteri": "group_name", "müşteri": "group_name",
  "otel": "hotel_name", "otel adı": "hotel_name", "otel adi": "hotel_name", "hotel": "hotel_name", "hotel name": "hotel_name",
  "giriş": "checkin", "giris": "checkin", "giris tarihi": "checkin", "giriş tarihi": "checkin",
  "çıkış": "checkout", "cikis": "checkout", "çıkış tarihi": "checkout", "cikis tarihi": "checkout",
  "yetişkin": "adults", "yetiskin": "adults",
  "çocuk": "children", "cocuk": "children",
  "görevli": "staff", "gorevli": "staff",
  "1k": "r1", "1 kişilik": "r1", "1 kisilik": "r1",
  "2k": "r2", "2 kişilik": "r2", "2 kisilik": "r2",
  "3k": "r3", "3 kişilik": "r3", "3 kisilik": "r3",
  "4k": "r4", "4 kişilik": "r4", "4 kisilik": "r4",
  "5k": "r5", "5 kişilik": "r5", "5 kisilik": "r5",
  "başlangıç": "meal_start", "baslangic": "meal_start", "başlangıç öğünü": "meal_start", "baslangic ogunu": "meal_start",
  "bitiş": "meal_end", "bitis": "meal_end", "bitiş öğünü": "meal_end", "bitis ogunu": "meal_end",
  "not": "notes", "notlar": "notes", "özel not": "notes", "ozel not": "notes",
};

function detectDelimiter(text) {
  const firstLine = (text.split(/\r?\n/)[0] || "").trim();
  const counts = { "\t": 0, ";": 0, ",": 0 };
  for (const ch of firstLine) if (counts[ch] !== undefined) counts[ch]++;
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
}

function splitLine(line, sep) {
  // Very small CSV splitter that honours double-quoted cells.
  const out = [];
  let cur = "";
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuote && line[i + 1] === '"') { cur += '"'; i++; }
      else inQuote = !inQuote;
    } else if (ch === sep && !inQuote) {
      out.push(cur);
      cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function parseDate(v) {
  if (!v) return "";
  const s = String(v).trim();
  // ISO already
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // dd/mm/yyyy or dd.mm.yyyy → yyyy-mm-dd
  const m = s.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return s;
}

function parseNumber(v, { defaultValue = 0 } = {}) {
  const raw = String(v ?? "").trim();
  if (raw === "") return defaultValue;
  const n = parseInt(raw.replace(/[^\d-]/g, ""), 10);
  return Number.isFinite(n) ? n : defaultValue;
}

function parseText(input) {
  const text = (input || "").replace(/\ufeff/g, "").trim();
  if (!text) return { headers: [], rows: [] };
  const sep = detectDelimiter(text);
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length);
  const headersRaw = splitLine(lines[0], sep);
  const headers = headersRaw.map((h) => HEADER_ALIASES[h.toLowerCase()] || h.toLowerCase());
  const rows = lines.slice(1).map((line) => {
    const cols = splitLine(line, sep);
    const obj = {};
    headers.forEach((h, i) => (obj[h] = cols[i] ?? ""));
    return {
      group_name: (obj.group_name || "").trim(),
      hotel_name: (obj.hotel_name || "").trim(),
      checkin: parseDate(obj.checkin),
      checkout: parseDate(obj.checkout),
      adults: parseNumber(obj.adults, { defaultValue: 0 }),
      children: parseNumber(obj.children),
      staff: parseNumber(obj.staff),
      rooms: {
        r1: parseNumber(obj.r1), r2: parseNumber(obj.r2),
        r3: parseNumber(obj.r3), r4: parseNumber(obj.r4),
        r5: parseNumber(obj.r5),
      },
      meal_start: MEAL_MAP[(obj.meal_start || "kahvalti").toLowerCase()] || "kahvalti",
      meal_end: MEAL_MAP[(obj.meal_end || "aksam").toLowerCase()] || "aksam",
      notes: obj.notes || "",
    };
  });
  return { headers: headersRaw, rows };
}

function validateRow(r) {
  const errors = [];
  if (!r.group_name) errors.push("Grup adı boş");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(r.checkin)) errors.push("Giriş tarihi hatalı");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(r.checkout)) errors.push("Çıkış tarihi hatalı");
  if (r.checkin && r.checkout && r.checkout < r.checkin) errors.push("Çıkış girişten önce");
  if (r.adults < 1) errors.push("Yetişkin en az 1");
  const rt = (r.rooms.r1 || 0) + (r.rooms.r2 || 0) + (r.rooms.r3 || 0) + (r.rooms.r4 || 0) + (r.rooms.r5 || 0);
  if (rt === 0) errors.push("En az bir oda gerekli");
  return errors;
}

const SAMPLE_CSV = `Grup Adı;Otel Adı;Giriş;Çıkış;Yetişkin;Çocuk;Görevli;1K;2K;3K;4K;5K;Başlangıç;Bitiş;Not
Ali Bey Grubu;Dar Al Eiman Al Andalus;15.03.2027;22.03.2027;18;2;1;0;7;1;0;0;kahvalti;aksam;Sabah 06:00 varış
Ayşe Hanım;Anjum Makkah;20.03.2027;27.03.2027;24;4;2;0;9;2;0;0;ogle;aksam;`;

export default function AcenteBulkImport() {
  const nav = useNavigate();
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [result, setResult] = useState(null);

  const { rows } = useMemo(() => parseText(raw), [raw]);
  const rowsWithErrors = rows.map((r) => ({ ...r, errors: validateRow(r) }));
  const validCount = rowsWithErrors.filter((r) => r.errors.length === 0).length;

  const readFile = (file) => {
    const name = (file?.name || "").toLowerCase();
    const isBinaryXlsx = /\.(xlsx|xls|xlsm|xlsb|ods)$/.test(name);
    if (isBinaryXlsx) {
      // Parse Excel binary via SheetJS → convert first sheet to CSV
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result);
          const wb = XLSX.read(data, { type: "array", cellDates: false });
          const firstSheetName = wb.SheetNames[0];
          if (!firstSheetName) {
            toast.error("Excel dosyasında sayfa bulunamadı");
            return;
          }
          const sheet = wb.Sheets[firstSheetName];
          // FS: semicolon so our current text parser works well with Turkish decimals.
          const csv = XLSX.utils.sheet_to_csv(sheet, { FS: ";", blankrows: false });
          setRaw(csv);
          toast.success(`${file.name} okundu (${firstSheetName})`);
        } catch (err) {
          toast.error("Excel dosyası okunamadı: " + (err?.message || "hatalı format"));
        }
      };
      reader.readAsArrayBuffer(file);
      return;
    }
    // Text-based (csv/tsv/txt)
    const reader = new FileReader();
    reader.onload = (e) => setRaw(String(e.target?.result || ""));
    reader.readAsText(file, "utf-8");
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) readFile(file);
  };

  const downloadTemplate = () => {
    const blob = new Blob(["\ufeff" + SAMPLE_CSV], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rezervasyon-sablonu.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadXlsxTemplate = () => {
    // Build a native .xlsx with the same headers/sample rows as the CSV template.
    const rows = [
      ["Grup Adı", "Otel Adı", "Giriş", "Çıkış", "Yetişkin", "Çocuk", "Görevli", "1K", "2K", "3K", "4K", "5K", "Başlangıç", "Bitiş", "Not"],
      ["Ali Bey Grubu", "Dar Al Eiman Al Andalus", "15.03.2027", "22.03.2027", 18, 2, 1, 0, 7, 1, 0, 0, "kahvalti", "aksam", "Sabah 06:00 varış"],
      ["Ayşe Hanım", "Anjum Makkah", "20.03.2027", "27.03.2027", 24, 4, 2, 0, 9, 2, 0, 0, "ogle", "aksam", ""],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 22 }, { wch: 22 }, { wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 7 }, { wch: 8 }, { wch: 5 }, { wch: 5 }, { wch: 5 }, { wch: 5 }, { wch: 5 }, { wch: 12 }, { wch: 12 }, { wch: 28 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Rezervasyonlar");
    XLSX.writeFile(wb, "rezervasyon-sablonu.xlsx");
  };

  const submit = async () => {
    const valid = rowsWithErrors.filter((r) => r.errors.length === 0).map(({ errors, ...rest }) => rest);
    if (valid.length === 0) return toast.error("Yüklenecek geçerli satır yok");
    setBusy(true);
    try {
      const { data } = await panelApi.post("/reservations/bulk", { rows: valid });
      setResult(data);
      toast.success(`${data.created} rezervasyon oluşturuldu`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="bulk-import-page">
      <div>
        <button onClick={() => nav(-1)} className="text-sm text-brand-navy/60 hover:text-brand-orange inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Geri
        </button>
        <div className="mt-2 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2">Toplu Yükleme</div>
            <h1 className="font-serif text-3xl text-brand-navy">Excel'den Rezervasyon İçe Aktar</h1>
            <p className="text-sm text-slate-600 mt-1">Elektronik tablonuzdan seçim yapın, buraya yapıştırın veya CSV sürükleyip bırakın.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download size={14} className="mr-1" /> CSV Şablonu
            </Button>
            <Button variant="outline" onClick={downloadXlsxTemplate} data-testid="download-xlsx-template-btn">
              <Download size={14} className="mr-1" /> Excel (.xlsx) Şablonu
            </Button>
          </div>
        </div>
      </div>

      <div
        className="bg-gradient-to-br from-brand-orange/5 to-brand-gold/10 rounded-2xl border-2 border-dashed border-brand-orange/40 p-6 text-center"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        data-testid="bulk-import-dropzone"
      >
        <FileSpreadsheet className="mx-auto text-brand-orange mb-3" size={40} />
        <p className="text-sm text-brand-navy font-medium">CSV veya Excel (.xlsx) dosyanızı buraya sürükleyin</p>
        <p className="text-xs text-slate-500 mt-1">veya</p>
        <div className="flex flex-wrap justify-center gap-3 mt-3">
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="upload-file-btn">
            <Upload size={14} className="mr-1" /> Dosya Seç
          </Button>
          <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls,.xlsm,.xlsb,.ods" hidden onChange={(e) => e.target.files?.[0] && readFile(e.target.files[0])} />
        </div>
        <p className="text-[11px] text-slate-500 mt-3">Desteklenen formatlar: .xlsx, .xls, .csv, .tsv</p>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <ClipboardPaste className="text-brand-orange" size={16} />
          <span className="text-sm font-medium text-brand-navy">veya Excel'den Kopyala-Yapıştır</span>
        </div>
        <Textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={8}
          placeholder="Excel'den satırları seçip Ctrl+C, sonra buraya Ctrl+V yapın..."
          className="font-mono text-xs"
          data-testid="bulk-import-textarea"
        />
      </div>

      {rows.length > 0 && (
        <div className="bg-white rounded-2xl border border-brand-navy/10 overflow-hidden" data-testid="bulk-import-preview">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <div className="text-sm">
              <span className="font-medium text-brand-navy">{rows.length}</span> satır bulundu ·
              <span className="text-emerald-600 font-medium ml-1">{validCount} geçerli</span> ·
              <span className="text-rose-600 font-medium ml-1">{rows.length - validCount} hatalı</span>
            </div>
            <Button onClick={submit} disabled={busy || validCount === 0} className="bg-brand-orange text-white" data-testid="bulk-import-submit">
              {busy ? "Yükleniyor..." : `${validCount} Rezervasyonu Oluştur`}
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-canvas-stone text-slate-500 uppercase">
                <tr>
                  <th className="p-2 text-left">#</th>
                  <th className="p-2 text-left">Grup</th>
                  <th className="p-2 text-left">Otel</th>
                  <th className="p-2">Giriş</th>
                  <th className="p-2">Çıkış</th>
                  <th className="p-2">Y/Ç/G</th>
                  <th className="p-2">1K/2K/3K/4K/5K</th>
                  <th className="p-2">Öğün</th>
                  <th className="p-2 text-left">Durum</th>
                </tr>
              </thead>
              <tbody>
                {rowsWithErrors.map((r, i) => (
                  <tr key={i} className={`border-b border-slate-100 ${r.errors.length ? "bg-rose-50/50" : ""}`} data-testid={`bulk-row-${i}`}>
                    <td className="p-2 text-slate-400">{i + 1}</td>
                    <td className="p-2 font-medium">{r.group_name || "—"}</td>
                    <td className="p-2 text-slate-600">{r.hotel_name || <span className="text-slate-300 italic">—</span>}</td>
                    <td className="p-2 text-center font-mono">{r.checkin || "—"}</td>
                    <td className="p-2 text-center font-mono">{r.checkout || "—"}</td>
                    <td className="p-2 text-center">{r.adults}/{r.children}/{r.staff}</td>
                    <td className="p-2 text-center">{r.rooms.r1}/{r.rooms.r2}/{r.rooms.r3}/{r.rooms.r4}/{r.rooms.r5}</td>
                    <td className="p-2 text-center text-slate-500">{r.meal_start} → {r.meal_end}</td>
                    <td className="p-2">
                      {r.errors.length === 0 ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 size={12}/> Hazır</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-rose-700 text-xs" title={r.errors.join(", ")}>
                          <AlertTriangle size={12}/> {r.errors[0]}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5" data-testid="bulk-import-result">
          <h3 className="font-serif text-lg text-brand-navy mb-3">Sonuç</h3>
          <div className="flex gap-6 text-sm mb-4">
            <div><span className="text-emerald-600 font-serif text-2xl">{result.created}</span> <span className="text-slate-500">oluşturuldu</span></div>
            <div><span className="text-rose-600 font-serif text-2xl">{result.failed}</span> <span className="text-slate-500">başarısız</span></div>
          </div>
          <div className="text-right">
            <Button onClick={() => nav("/acente/rezervasyonlar")} className="bg-brand-navy text-white">Rezervasyonlara Git</Button>
          </div>
        </div>
      )}
    </div>
  );
}
